import AssetImage from '@/presentation/components/elements/AssetImage';
import VaultIcon from '@/presentation/components/elements/VaultIcon';
import Collapsible from '@/presentation/components/modules/Collapsible';
import EligibleVaults from '@/presentation/components/modules/EligibleVaults';
import { useModalContext } from '@/presentation/context/modal';
import { Asset, Collection, VTokenBalance, addressEqual } from 'nftx.js';
import { useState } from 'react';
import { QueryStatus } from '@tanstack/react-query';
import { useTranslations } from 'use-intl';

const YourItems = ({
  slug,
  vaultIds,
  collection,
  collectionAssets,
  status,
}: {
  vaultIds: string[];
  collection?: Collection & { vTokens: VTokenBalance[] };
  collectionAssets?: Asset[];
  slug: string;
  status: QueryStatus;
}) => {
  const t = useTranslations('Profile.Balances');
  const { showAssetDetailModal, showCreatePositionModal } = useModalContext();
  const [showHint, setShowHint] = useState(false);
  const totalCount =
    collectionAssets?.reduce((total, asset) => {
      return total + Number(asset.quantity ?? '1');
    }, 0) ?? 0;
  const itemsPreview =
    collectionAssets
      ?.slice(0, 11 - (collection?.vTokens.length || 0))
      .filter(Boolean) || [];
  const previewCount = itemsPreview.reduce((total, asset) => {
    return total + Number(asset.quantity ?? '1');
  }, 0);

  const itemsDiff = totalCount - previewCount;

  const vTokenOwned = collection?.vTokens.filter((v) => v.balance > 0) || [];

  return (
    <div className="space-y-4">
      <Collapsible
        header={
          <h3 className="font-bold">
            {t('collection.items', {
              count: totalCount + vTokenOwned.length,
            })}
          </h3>
        }
        open={true}
      >
        <div className="grid grid-cols-6 md:grid-cols-8 lg:grid-cols-12 gap-3 p-4">
          {collection?.vTokens.map((v) => (
            <VaultIcon
              vaultId={v.vaultId}
              key={v.vaultId}
              className="aspect-square cursor-pointer outline-pink-500 hover:outline hover:outline-1 transition-colors"
              onClick={() =>
                showCreatePositionModal({
                  isNewVault: false,
                  using: 'vtoken',
                  vaultId: v.vaultId,
                })
              }
            />
          ))}
          {itemsPreview?.map((c) => (
            <div key={c.id} className="relative">
              <AssetImage
                {...c}
                className="cursor-pointer outline-pink-500 hover:outline hover:outline-1 transition-colors"
                onClick={() =>
                  showAssetDetailModal({
                    tokenId: c.tokenId,
                    assetAddress: c.assetAddress,
                    quantity: c.quantity,
                    collectionSlug: slug,
                  })
                }
              />
              {collection?.standard === 'ERC1155' && (
                <div className="absolute top-0 left-0 bg-white/70 text-xs px-1 py-0.5 rounded">
                  {`x${c.quantity}`}
                </div>
              )}
            </div>
          ))}
          {itemsDiff > 0n && (
            <span
              className="grid place-content-center cursor-pointer hover:outline hover:outline-1 outline-pink-500"
              onClick={() => setShowHint(true)}
            >
              {'+'}
              {itemsDiff?.toString()}
            </span>
          )}
        </div>
      </Collapsible>

      <EligibleVaults
        slug={slug}
        vaultIds={vaultIds}
        collection={collection}
        collectionAssets={collectionAssets}
        status={status}
        showHint={showHint}
      />
    </div>
  );
};

export default YourItems;
